/**
 * @file Questions and Answers.
 */

import { gsap } from 'gsap';
import scrollTo from 'gsap/ScrollToPlugin';
import debounce from 'just-debounce';
import throttle from 'just-throttle';
import clone from 'just-clone';

gsap.registerPlugin(scrollTo);

/**
 * Q+A.
 */
const questionAnswer = () => {
	const sidebar = document.querySelector('.v-qa-sidebar');
	if (!sidebar) return;
	const content = document.querySelector('.v-qa-content');

	const links = [...sidebar.querySelectorAll('.v-qa-sidebar__link')].filter((l) => l.getAttribute('href').replace('#', '').length);
	const sections = links.map((l) => content.querySelector(l.getAttribute('href')));
	const qButtons = [...document.querySelectorAll('.v-qa-content__trigger')];

	let offsets;

	/**
	 * Set question offsets.
	 */
	const setOffsets = () => {
		offsets = sections.map((s) => {
			const sectionSizing = s.getBoundingClientRect();
			return {
				id: s.id,
				y:  sectionSizing.top + sectionSizing.height / 2 + window.pageYOffset,
			};
		});
	};
	setOffsets();
	window.addEventListener('resize', debounce(setOffsets, 100));

	let winHeight = window.innerHeight;
	window.addEventListener('resize', debounce(() => { winHeight = window.innerHeight; }, 100));

	let currentSection = sidebar.querySelector('.v-qa-sidebar__link--active');

	/**
	 * Set current question.
	 */
	const setCurrent = () => {
		const y = window.pageYOffset + winHeight / 4;
		const closest = clone(offsets).map((o) => {
			o.y = y - o.y;
			return o;
		})
			.filter((o) => o.y > 0)
			.sort((a, b) => {
				if (a.y === b.y) return 0;
				return a.y < b.y ? -1 : 1;
			});

		const newCurrent = sidebar.querySelector(`[href='#${closest.length ? closest[0].id : sections[0].id}']`);
		if (newCurrent === currentSection) return;

		if (currentSection) {
			currentSection.classList.remove('v-qa-sidebar__link--active');
			currentSection.parentElement.classList.remove('v-qa-sidebar__item--active');
			currentSection.removeAttribute('aria-current');
		}
		currentSection = newCurrent;
		currentSection.classList.add('v-qa-sidebar__link--active');
		currentSection.parentElement.classList.add('v-qa-sidebar__item--active');
		currentSection.setAttribute('aria-current', 'true');
	};
	setCurrent();
	window.addEventListener('scroll', throttle(setCurrent, 50));

	/**
	 * Scroll to question.
	 *
	 * @param {string} href - Link href.
	 */
	const scrollToSection = (href) => {
		const target = content.querySelector(href);
		const distance = target.getBoundingClientRect().top;
		const offset = distance + window.pageYOffset - 20;
		gsap.to(window, {
			duration:   0.5,
			scrollTo:   offset,
			onComplete: () => {
				target.focus();
			},
		});
	};

	[...links].forEach((l) => l.addEventListener('click', (e) => {
		e.preventDefault();
		scrollToSection(l.getAttribute('href'));
	}));

	/**
	 * Scroll to individual question from hash.
	 *
	 * @param {string} hash - Hash of question.
	 */
	const scrollToQuestion = (hash) => {
		const target = document.querySelector(hash);
		const distance = target.getBoundingClientRect().top;
		const offset = distance + window.pageYOffset - 20;
		gsap.to(window, {
			duration:   0.5,
			scrollTo:   offset,
			onComplete: () => {
				target.click();
				target.querySelector('.v-qa-content__question').focus();
			},
		});
	};

	/* -------------------------------------*\
	  Accessible dropdown menu
	\*-------------------------------------*/
	const visibleQ    = window.matchMedia('(max-width: 1024px)');
	const selectWrap  = document.querySelector('.v-qa-sidebar__mobile-nav');
	const selectLabel = document.querySelector('#v-qa-sidebar__mobile-label');
	const selectBtn   = document.querySelector('.v-qa-sidebar__select--btn');
	const selectList  = document.querySelector('.v-qa-sidebar__select--list');
	const selectLinks = selectList.querySelectorAll('.v-qa-sidebar__select-link');
	let visible     = false;

	const shrink = gsap.to(selectList, {
		duration: 0.5,
		paused:   true,
		ease:     'easeInOut',
		height:   '0px',
		opacity:  0,
		onStart:  () => {
			selectBtn.innerText = 'Select a Topic';
			selectBtn.setAttribute('aria-label', 'Select a Topic - making a selection will navigate to that topic');
			selectLabel.classList.remove('v-form__label-text--float-up');
		},
		onComplete: () => {
			selectList.classList.add('v-qa-sidebar__select--hidden');
			selectList.setAttribute('aria-activedescendant', '');
			selectList.blur();
		},
	});

	/**
	 * Close.
	 */
	const closeCurtain = () => {
		selectBtn.removeAttribute('aria-expanded');
		selectBtn.parentElement.classList.remove('v-form__select--expanded');
		shrink.restart();
	};

	selectBtn.addEventListener('click', (e) => {
		e.preventDefault();

		const expanded     = e.target.hasAttribute('aria-expanded');
		const { target } = e;
		const parent     = target.parentElement;

		if (!expanded) {
			target.setAttribute('aria-expanded', 'true');
			parent.classList.add('v-form__select--expanded');
			gsap.to(selectList, {
				duration:   0.4,
				ease:       'easeOut',
				height:     '60vh',
				overflowX:  'visible',
				visibility: 'visible',
				opacity:    1,
				onStart:    () => {
					selectList.classList.remove('v-qa-sidebar__select--hidden');
					selectLabel.classList.add('v-form__label-text--float-up');
					selectBtn.innerText = 'Close';
					selectBtn.setAttribute('aria-label', 'Close the topics list');
				},
				onComplete: () => {
					selectList.focus();
				},
			});
		} else {
			closeCurtain();
		}
	});

	selectBtn.addEventListener('focusin', () => {
		selectLabel.classList.add('v-form__label-text--float-up');
	});

	document.addEventListener('focusin', (e) => {
		const { target } = e;

		if (!selectWrap.contains(target) && selectBtn.hasAttribute('aria-expanded')) {
			closeCurtain();
		}

		if (selectBtn === target && selectBtn.hasAttribute('aria-expanded')) {
			selectList.setAttribute('aria-activedescendant', '');
		}

		if (!selectWrap.contains(target) && selectLabel.classList.contains('v-form__label-text--float-up')) {
			selectLabel.classList.remove('v-form__label-text--float-up');
		}

		/* if ( selectList.contains( target ) && ! selectBtn.hasAttribute( 'aria-expanded' ) ) {
			// console.log( 'select list contains target');
			// console.log(' button has attribute? ' + selectBtn.hasAttribute( 'aria-expanded' ) );
			// if ( selectList.hasAttribute( 'aria-activedescendant' ) ) {
				// }
			selectList.removeAttribute( 'aria-activedescendant' );
			target.blur();
			selectBtn.focus();
		} */
	});

	selectWrap.addEventListener('focusin', (e) => {
		const { target } = e;
		const parent     = target.parentElement;
		if (parent.classList.contains('v-qa-sidebar__select-item')) {
			parent.setAttribute('aria-selected', true);
			selectList.setAttribute('aria-activedescendant', parent.id);
		}
	});

	selectWrap.addEventListener('keydown', (e) => {
		if (e.code === 'Escape') {
			closeCurtain();
		}
	});

	selectLinks.forEach((l) => {
		l.addEventListener('focusout', (e) => {
			e.target.parentElement.setAttribute('aria-selected', false);
		});
	});

	selectList.addEventListener('click', (e) => {
		e.preventDefault();

		const { target } = e;
		const link       = target.closest('.v-qa-sidebar__select-link');

		if (link) {
			scrollToSection(link.getAttribute('href'));
		}
	});

	/* const selectNav = document.querySelector('.v-qa-sidebar__select');
  selectNav.addEventListener('change', e => {
    if (!selectNav.value) return;
    scrollToSection(selectNav.value);
  }); */

	/**
	 * Toggle accessible dropdown visibility to assistive tech.
	 */
	const toggleAccessible = () => {
		selectList.setAttribute('aria-hidden', true);

		if (!visible) {
			selectList.setAttribute('aria-hidden', false);
			visible = true;
		}
	};

	visibleQ.addEventListener('change', toggleAccessible);

	if (visibleQ.matches) {
		toggleAccessible();
	}

	/* ------------ End accessible dropdown---------------*/

	const questions = content.querySelectorAll('.v-qa-content__trigger');
	questions.forEach((q) => q.addEventListener('click', () => {
		const answer = q.nextElementSibling;
		const answerInner = answer.querySelector('.v-qa-content__answer-inner');

		if (answer.classList.contains('open')) {
			gsap.to(answer, {
				height:     0,
				duration:   0.3,
				onComplete: () => {
					answer.classList.remove('open');
					answer.removeAttribute('style');
					q.setAttribute('aria-expanded', 'false');
					setOffsets();
				},
			});
		} else {
			answer.style.display = 'block';
			gsap.to(answer, {
				height:     answerInner.getBoundingClientRect().height,
				duration:   0.3,
				onComplete: () => {
					answer.classList.add('open');
					answer.removeAttribute('style');
					q.setAttribute('aria-expanded', 'true');
					setOffsets();
				},
			});
		}
	}));

	if (window.location.hash) {
		const target = content.querySelector(window.location.hash);

		if (qButtons.includes(target)) {
			scrollToQuestion(window.location.hash);
		} else if (sections.includes(target)) {
			scrollToSection(window.location.hash);
		}
	}
};

export default questionAnswer;
